import React from 'react';
import ReactDOM from 'react-dom';
import ReactCrop, { type Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

export interface CropHandler {
    crop(): void;
    cancel(): void;
}

export class CropHandlerImpl implements CropHandler {
    private canvas: HTMLDivElement;
    private background: HTMLCanvasElement;
    private container: HTMLDivElement | null = null;
    private originalImageSrc: string | null = null;
    private overlayContainer: HTMLDivElement | null = null;
    private hasActiveCrop: boolean = false;

    constructor(canvas: HTMLDivElement, background: HTMLCanvasElement) {
        this.canvas = canvas;
        this.background = background;
    }

    public crop(): void {
        if (this.container) return;

        this.cleanup();

        const canvasContainer = this.canvas.closest('.cvat-canvas-container');
        if (!canvasContainer) {
            console.error('Could not find .cvat-canvas-container');
            return;
        }

        const backgroundRect = this.background.getBoundingClientRect();
        const computedStyle = getComputedStyle(this.background);
        const transform = computedStyle.transform;

        if (!transform || transform === 'none') {
            console.error('No transform matrix found');
            return;
        }

        const matrixMatch = transform.match(/^matrix\(([^)]+)\)$/);
        if (!matrixMatch) {
            console.error('Invalid transform format');
            return;
        }

        const matrixValues = matrixMatch[1].split(',').map(parseFloat);
        const [a, , , d, e, f] = matrixValues;

        const scaleX = a;
        const scaleY = d;
        const offsetX = e;
        const offsetY = f;

        const renderedWidth = this.background.width * scaleX;
        const renderedHeight = this.background.height * scaleY;

        this.originalImageSrc = this.background.toDataURL();

        this.overlayContainer = document.createElement('div');
        Object.assign(this.overlayContainer.style, {
            position: 'absolute',
            top: '0px',
            left: '0px',
            width: '100%',
            height: '100%',
            zIndex: '100',
            pointerEvents: 'auto',
        });
        canvasContainer.appendChild(this.overlayContainer);

        this.container = document.createElement('div');
        Object.assign(this.container.style, {
            position: 'absolute',
            left: `${backgroundRect.left - canvasContainer.getBoundingClientRect().left}px`,
            top: `${backgroundRect.top - canvasContainer.getBoundingClientRect().top}px`,
            width: `${renderedWidth}px`,
            height: `${renderedHeight}px`,
            pointerEvents: 'auto',
            overflow: 'hidden',
            zIndex: 110
        });

        this.overlayContainer.appendChild(this.container);

        const darken = document.createElement('div');
        Object.assign(darken.style, {
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            pointerEvents: 'none',
            zIndex: 100,
        });
        this.overlayContainer.appendChild(darken);

        const CropComponent = (): React.ReactElement => {
            const [crop, setCrop] = React.useState<Crop>({
                unit: 'px',
                x: 0,
                y: 0,
                width: 0,
                height: 0,
            });

            const onCropUpdate = (c: Crop, percentCrop: Crop): void => {
                this.hasActiveCrop = !!c.width && !!c.height;

                if (this.hasActiveCrop) {
                    const left = Math.round((c.x - offsetX) / scaleX);
                    const top = Math.round((c.y - offsetY) / scaleY);
                    const right = Math.round((c.x + c.width - offsetX) / scaleX);
                    const bottom = Math.round((c.y + c.height - offsetY) / scaleY);

                    this.canvas.dispatchEvent(new CustomEvent('canvas.cropped', {
                        detail: { points: [left, top, right, bottom] }
                    }));
                } else {
                    this.canvas.dispatchEvent(new CustomEvent('canvas.cropped', {
                        detail: null
                    }));
                }
            };

            return React.createElement(
                ReactCrop,
                {
                    crop,
                    onChange: setCrop,
                    onComplete: onCropUpdate,
                    ruleOfThirds: true,
                },
                React.createElement('img', {
                    src: this.originalImageSrc || '',
                    alt: 'Crop target',
                    style: {
                        width: `${renderedWidth}px`,
                        height: `${renderedHeight}px`,
                        objectFit: 'contain',
                        display: 'block'
                    }
                })
            );
        };

        ReactDOM.render(React.createElement(CropComponent), this.container);
    }

    public cancel(): void {
        this.cleanup();
        this.canvas.dispatchEvent(new CustomEvent('canvas.canceled'));
    }

    private cleanup(): void {
        if (this.container) {
            ReactDOM.unmountComponentAtNode(this.container);
            this.container.remove();
            this.container = null;
        }

        if (this.overlayContainer) {
            this.overlayContainer.remove();
            this.overlayContainer = null;
        }

        this.originalImageSrc = null;
    }
}